import Vue from 'vue';
import moment from 'moment';
import { isInCurrentWeek } from '@/helpers/DateHelpers';
import CmsService from '@/services/cms/CmsService';
import UndeliverableDatesService from '@/services/cms/UndeliverableDatesService';

const getDefaultState = () => {
    return {
        rateableArrangements: {
            fetched: false,
            items: null,
        },
        schedules: {
            fetched: false,
            fetchedAt: null,
            items: null,
            activeWeekStart: '',
        },
        undeliverableDates: {
            gifting: {
                fetched: false,
                items: null,
            },
        },
        plans: [],
    };
};

export default {
    namespaced: true,
    state: getDefaultState(),
    mutations: {
        resetState(state) {
            Object.assign(state, getDefaultState());
        },

        // Reset the currently selected date.
        resetDeliveryDate: function(state) {
            // Sunday should be the first day of the week in this component, so to be independent on the timezone,
            // we use 'isoWeek' (which is always Monday) and subtract one day
            state.schedules.activeWeekStart = moment()
                .startOf('isoWeek')
                .subtract(1, 'day')
                .format('YYYY-MM-DD');
        },

        // Update the state rateable arrangements.
        updateRateableArrangements: (state, payload) => state.rateableArrangements.items = payload,

        // Update the state schedules.
        updateSchedules: (state, payload) => state.schedules.items = payload,

        updatePlans: (state, payload) => state.plans = payload,

        // Update the gifting undeliverable dates.
        updateGiftingUndeliverableDates: (state, payload) => state.undeliverableDates.gifting.items = payload,

        // Update the state schedules.
        updateSchedulesActiveWeekStart: (state, payload) => {
            Vue.set(state.schedules, 'activeWeekStart', payload);
        },
    },
    actions: {
        async fetchRateableArrangements({ commit, state }, forceReload = false) {
            if (state.rateableArrangements.fetched === false || forceReload === true) {
                state.rateableArrangements.fetched = true;

                let service = new CmsService();

                return new Promise(function(resolve, reject) {
                    service.getRateableArrangements()
                        .then(response => {
                            commit('updateRateableArrangements', response.data.items);
                            resolve(response);
                        })
                        .catch(error => {
                            state.rateableArrangements.fetched = false;
                            reject(error);
                        });
                });
            }
        },

        async fetchSchedules({ commit, state }, forceReload = false) {
            if (moment().format('YYYY-MM-DD') !== state.schedules.fetchedAt) {
                forceReload = true;
            }

            if (state.schedules.fetched === false || forceReload === true) {
                state.schedules.fetched = true;
                state.schedules.fetchedAt = moment().format('YYYY-MM-DD');

                let service = new CmsService();

                return new Promise(function(resolve, reject) {
                    service.getAllSchedules()
                        .then(response => {
                            commit('updateSchedules', response.data.data);
                            resolve(response);
                        })
                        .catch(error => {
                            state.schedules.fetched = false;
                            reject(error);
                        });
                });
            }
        },

        async fetchPlans({ commit, state, rootGetters }, forceReload = false) {
            let service = new CmsService();
            return new Promise(function(resolve, reject) {
                service.getPlans().then(response => {
                    commit('updatePlans', response.data.data);
                }).catch(error => console.error(error));
            });
        },

        async fetchGiftingUndeliverableDates({ commit, state }, forceReload = false) {
            if (state.undeliverableDates.gifting.fetched === false || forceReload === true) {
                state.undeliverableDates.gifting.fetched = true;

                let service = new UndeliverableDatesService();

                return new Promise(function(resolve, reject) {
                    service.getGiftingUndeliverableDates()
                        .then(response => {
                            commit('updateGiftingUndeliverableDates', response.data.data.map(date => {
                                return new Date(date.date);
                            }));
                            resolve(response);
                        })
                        .catch(error => {
                            state.undeliverableDates.gifting.fetched = false;
                            reject(error);
                        });
                });
            }
        },

        incrementActiveScheduleWeek({ commit, state }) {
            let week = moment(state.schedules.activeWeekStart);

            commit('updateSchedulesActiveWeekStart', week.add(1, 'week').format('YYYY-MM-DD'));
        },

        decrementActiveScheduleWeek({ commit, state }) {
            let week = moment(state.schedules.activeWeekStart);

            commit('updateSchedulesActiveWeekStart', week.subtract(1, 'week').format('YYYY-MM-DD'));
        },

        setActiveScheduleWeek({ commit, state }, week) {
            commit('updateSchedulesActiveWeekStart', week.format('YYYY-MM-DD'));
        },

        // Reset CMS module state.
        resetCms({ commit }) {
            commit('resetState');
        },
    },
    getters: {
        getRateableArrangements: state => state.rateableArrangements.items || [],

        getSchedules: state => state.schedules.items || [],

        getGiftingUndeliverableDates: state => state.undeliverableDates.gifting.items || [],

        // Get the first day of the active week.
        getActiveWeekStart: state => state.activeWeekStart,

        // Gets recent schedule for arrangement type A discluding the upcoming arrangement
        getRecentArrangements(state, getters, actions) {
            const arrangementType = 'A';
            const allArrangements = getters.getSchedules;
            const filtered = allArrangements.filter(schedule => schedule.arrangement === arrangementType);
            const numberOfArrangements = filtered.length;
            const finalArray = filtered.slice(1, numberOfArrangements);
            return finalArray;
        },

        getPlans: state => state.plans,

        // Get the schedules for the active subscription.
        getSchedulesForActiveSubscription(state, getters, actions, rootGetters) {
            let activeSubscription = rootGetters['subscription/getActiveSubscription'];

            if (activeSubscription) {
                let activeArrangement = activeSubscription.arrangement;

                return getters.getSchedules
                    .filter(schedule => schedule.arrangement === activeArrangement);
            }

            return [];
        },

        // Get the active schedule based on the current active week and subscription.
        getActiveSchedule(state, getters, actions, rootGetters) {
            if (!getters.getSchedulesForActiveSubscription.length) {
                return null;
            }

            return getters.getSchedulesForActiveSubscription
                .find(schedule => {
                    const startOfWeek = moment(state.schedules.activeWeekStart);
                    const deliveriesStartAt = moment(schedule.deliveries_start_at);

                    return isInCurrentWeek(deliveriesStartAt, startOfWeek);
                });
        },

        // Get the active schedule delivery day based on the user's active subscription.
        getActiveScheduleDeliveryDate(state, getters, actions, rootGetters) {
            let activeSubscription = rootGetters['subscription/getActiveSubscription'];

            if (activeSubscription) {
                let nextDeliveryDate = moment(activeSubscription.next_delivery_date);
                let dayNumber = nextDeliveryDate.clone().day();
                let startOfWeek = state.schedules.activeWeekStart;

                // week starts on Sat, so add the weekday number to get the delivery day for the user's subscription
                return moment(startOfWeek).add(dayNumber, 'd');
            }

            return null;
        },

        // Determine whether the current active schedule can be rated.
        canRateActiveSchedule(state, getters, actions, rootGetters) {
            let rateableSchedules = getters.getRateableArrangements;
            let activeSchedule = getters.getActiveSchedule;
            let activeSubscription = rootGetters['subscription/getActiveSubscription'];

            if (rateableSchedules.length && activeSchedule) {
                return rateableSchedules.find(item => {
                    return item.id === activeSchedule.id &&
                        item.subscription.id === activeSubscription.id;
                });
            }

            return false;
        },

        // Determine whether there are schedules before the current active schedule for the active subscription
        // and arrangement.

        canDecrementActiveSchedule(state, getters) {
            let activeSchedule = getters.getActiveSchedule;

            if (activeSchedule) {
                let schedules = getters.getSchedulesForActiveSubscription;

                // Date to search for for the arrangement schedules
                let previousWeek = moment(activeSchedule.deliveries_start_at)
                    .subtract(1, 'week')
                    .format('YYYY-MM-DD');

                return schedules.find(item => item.deliveries_start_at === previousWeek);
            }

            return false;
        },

        // Determine whether there are schedules after the current active schedule for the active subscription
        // and arrangement.
        canIncrementActiveSchedule(state, getters) {
            let activeSchedule = getters.getActiveSchedule;

            if (activeSchedule) {
                let schedules = getters.getSchedulesForActiveSubscription;

                // Date to search for for the arrangement schedules
                let followingWeek = moment(activeSchedule.deliveries_start_at)
                    .add(1, 'week')
                    .format('YYYY-MM-DD');

                return schedules.find(item => item.deliveries_start_at === followingWeek);
            }

            return false;
        },
    },
};
